import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TeacherEnquiryReply } from "@/components/TeacherEnquiryReply";

const when = (s) => new Date(s).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

export default function AdminMore() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => { if (user?.role === "admin") api.get("/admin/more").then(({ data }) => setD(data)).catch(() => setD({ interest: [], enquiries: [], courses: {} })); }, [user]);
  if (user?.role !== "admin") return <p className="p-10" data-testid="admin-more-denied">Admins only.</p>;
  if (!d) return <p className="p-10" data-testid="admin-more-loading">Loading…</p>;
  return (
    <main className="mx-auto max-w-5xl space-y-8 px-6 py-10" data-testid="admin-more-page">
      <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground" data-testid="admin-more-back"><ArrowLeft size={14} /> Admin</Link>
      <h1 className="font-serif text-4xl">More from Jason</h1>
      <p className="text-sm text-muted-foreground">Band enquirers get your biography automatically. Call them first, then send the contract &amp; requirements yourself once they confirm.</p>
      <section className="rounded-3xl border border-border bg-card p-6" data-testid="admin-band-enquiries">
        <h2 className="font-serif text-xl">JT Band enquiries ({d.enquiries.length})</h2>
        {d.enquiries.length === 0 && <p className="mt-2 text-sm text-muted-foreground">None yet.</p>}
        <ul className="mt-4 space-y-3">
          {d.enquiries.map((e, i) => (
            <li key={i} className="rounded-2xl border border-border p-4 text-sm" data-testid={`band-enquiry-${i}`}>
              <p className="font-semibold">{e.name} · <a href={`tel:${e.phone}`} className="text-primary" data-testid={`band-enquiry-phone-${i}`}>{e.phone}</a>{e.replied_at && <span className="ml-2 rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs text-emerald-600 dark:text-emerald-400" data-testid={`band-enquiry-replied-${i}`}>Replied</span>}</p>
              <p className="text-muted-foreground">{e.event_date} at {e.event_time} · <a href={`mailto:${e.email}`} className="text-primary">{e.email}</a> · received {when(e.created_at)}</p>
              {e.details && <p className="mt-2 whitespace-pre-line">{e.details}</p>}
              <TeacherEnquiryReply kind="band" enquiry={e} i={i} onReplied={(r) => setD((p) => ({ ...p, enquiries: p.enquiries.map((x) => x.id === e.id ? { ...x, replied_at: r.sent_at } : x) }))} />
            </li>
          ))}
        </ul>
      </section>
      <section className="rounded-3xl border border-border bg-card p-6" data-testid="admin-teacher-enquiries">
        <h2 className="font-serif text-xl">Class Teaching enquiries ({(d.teacher_enquiries || []).length})</h2>
        {!(d.teacher_enquiries || []).length && <p className="mt-2 text-sm text-muted-foreground">None yet.</p>}
        <ul className="mt-4 space-y-3">
          {(d.teacher_enquiries || []).map((e, i) => (
            <li key={i} className="rounded-2xl border border-border p-4 text-sm" data-testid={`teacher-enquiry-${i}`}>
              <p className="font-semibold">{e.name} · {e.school}{e.phone && <> · <a href={`tel:${e.phone}`} className="text-primary">{e.phone}</a></>}{e.replied_at && <span className="ml-2 rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs text-emerald-600 dark:text-emerald-400" data-testid={`teacher-enquiry-replied-${i}`}>Replied</span>}</p>
              <p className="text-muted-foreground"><a href={`mailto:${e.email}`} className="text-primary">{e.email}</a> · received {when(e.created_at)}</p>
              <p className="mt-2 whitespace-pre-line">{e.message}</p>
              <TeacherEnquiryReply enquiry={e} i={i} onReplied={(r) => setD((p) => ({ ...p, teacher_enquiries: p.teacher_enquiries.map((x) => x.id === e.id ? { ...x, replied_at: r.sent_at } : x) }))} />
            </li>
          ))}
        </ul>
      </section>
      <section className="rounded-3xl border border-border bg-card p-6" data-testid="admin-course-interest">
        <h2 className="font-serif text-xl">Coming-soon interest</h2>
        <div className="mt-4 grid gap-4 md:grid-cols-3">
          {Object.entries(d.courses).map(([id, title]) => {
            const list = d.interest.filter((x) => x.course === id);
            return (
              <div key={id} className="rounded-2xl border border-border p-4" data-testid={`interest-${id}`}>
                <p className="font-semibold">{title}</p>
                <p className="text-sm text-primary">{list.length} waiting</p>
                <ul className="mt-2 max-h-48 space-y-1 overflow-y-auto text-xs text-muted-foreground">{list.map((x) => <li key={x.email}>{x.email}</li>)}</ul>
                {list.length > 0 && <button type="button" onClick={() => navigator.clipboard.writeText(list.map((x) => x.email).join(", ")).then(() => toast.success("Emails copied"))}
                  className="mt-3 text-xs font-semibold text-primary underline" data-testid={`interest-copy-${id}`}>Copy all emails</button>}
              </div>
            );
          })}
        </div>
      </section>
    </main>
  );
}
